import { useState, useCallback, useEffect } from 'react'
import req from '../../../../../api/request'
import { UpcomingGame } from '../../../../../types/PlayerAttendance'

interface AttendanceState {
  attendance: UpcomingGame[]
  isLoading: boolean
  error: string | null
}

interface UseAttendanceResult {
  attendanceState: AttendanceState
  readAttendanceInformation: (refresh?: boolean) => Promise<void>
  updateAttendanceStatus: (
    playerId: string,
    gameId: string,
    rosterId: string,
    status: number | null
  ) => Promise<void>
  updateBringingRefreshments: (params: {
    gameId: string
    rosterId: string
    playerId?: string
    officialId?: string
  }) => Promise<void>
}

interface UseAttendanceOptions {
  autoFetch?: boolean
}

export const useAttendance = (
  options: UseAttendanceOptions = {}
): UseAttendanceResult => {
  const { autoFetch = true } = options

  const [attendanceState, setAttendanceState] = useState<AttendanceState>({
    attendance: [],
    isLoading: false,
    error: null,
  })

  const readAttendanceInformation = useCallback(async (refresh = true) => {
    if (refresh) {
      setAttendanceState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }))
    }

    try {
      const attendanceData: UpcomingGame[] = []

      const { data: playerAttendanceData } = await req(
        '/home/player-attendance'
      )
      if (playerAttendanceData?.upcomingGames) {
        attendanceData.push(...playerAttendanceData.upcomingGames)
      }

      // Fetch team official attendance data
      const { data: teamOfficialAttendanceData } = await req(
        '/home/team-official-attendance'
      )
      if (teamOfficialAttendanceData?.upcomingGames) {
        teamOfficialAttendanceData.upcomingGames.forEach(
          (teamOfficialAttendance: UpcomingGame) => {
            const index = attendanceData.findIndex(
              (attendance) => attendance.id === teamOfficialAttendance.id
            )
            if (index !== -1) {
              const existing = attendanceData[index]
              if (existing.home_team && !existing.home_team.videographer_user) {
                existing.home_team.videographer_user =
                  teamOfficialAttendance.home_team?.videographer_user
              }
              /*
               * Guarded: a placeholder-visitor game has NO `visiting_team` at
               * all -- the key is omitted, not null -- so the bare property
               * read this replaced threw a TypeError and killed the whole
               * attendance fetch, blanking every game widget (SN-8410).
               */
              if (
                existing.visiting_team &&
                !existing.visiting_team.videographer_user
              ) {
                existing.visiting_team.videographer_user =
                  teamOfficialAttendance.visiting_team?.videographer_user
              }
              existing.myTeamOfficials = teamOfficialAttendance.myTeamOfficials
              existing.show_waiver = teamOfficialAttendance.show_waiver
              existing.show_video = teamOfficialAttendance.show_video
              /*
               * Both endpoints send the placeholder-visitor fields, so carry
               * them across rather than keeping only what player-attendance
               * happened to return. Filled only when the team-official payload
               * actually has them: a blind copy would write `undefined` over a
               * value the player payload already supplied, turning a
               * placeholder game back into a nameless one (SN-8410).
               */
              if (teamOfficialAttendance.has_placeholder_visitor != null) {
                existing.has_placeholder_visitor =
                  teamOfficialAttendance.has_placeholder_visitor
              }
              if (
                teamOfficialAttendance.visiting_team_placeholder_name != null
              ) {
                existing.visiting_team_placeholder_name =
                  teamOfficialAttendance.visiting_team_placeholder_name
              }
            } else {
              attendanceData.push(teamOfficialAttendance)
            }
          }
        )
      }

      // Sort and filter games
      const sortedGames = attendanceData
        .sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
        )
        .filter(
          (game) =>
            (game.myPlayers?.length ?? 0) > 0 ||
            (game.myTeamOfficials?.length ?? 0) > 0
        )

      setAttendanceState((prevState) => ({
        ...prevState,
        attendance: sortedGames,
        isLoading: false,
        error: null,
      }))
    } catch (e) {
      setAttendanceState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: 'An error occurred when trying to read attendance',
      }))
    }
  }, [])

  const updateAttendanceStatus = useCallback(
    async (
      playerId: string,
      gameId: string,
      rosterId: string,
      status: number | null
    ) => {
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/players/${playerId}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({ attendance_status: status }),
          }
        )
        await readAttendanceInformation(false)
      } catch (e) {
        setAttendanceState((prevState) => ({
          ...prevState,
          error: 'An error occurred when updating attendance status',
        }))
      }
    },
    [readAttendanceInformation]
  )

  const updateBringingRefreshments = useCallback(
    async ({
      gameId,
      rosterId,
      playerId,
      officialId,
    }: {
      gameId: string
      rosterId: string
      playerId?: string
      officialId?: string
    }) => {
      const segment = officialId
        ? `team-officials/${officialId}`
        : `players/${playerId}`
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/${segment}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({ bringing_refreshments: true }),
          }
        )
        await readAttendanceInformation(false)
      } catch (e) {
        setAttendanceState((prevState) => ({
          ...prevState,
          error: 'An error occurred when updating refreshments',
        }))
      }
    },
    [readAttendanceInformation]
  )

  useEffect(() => {
    if (autoFetch) {
      readAttendanceInformation()
    }
  }, [autoFetch, readAttendanceInformation])

  return {
    attendanceState,
    readAttendanceInformation,
    updateAttendanceStatus,
    updateBringingRefreshments,
  }
}
